/**
 * Dry-run state machine — OutpostForge frontend
 * Enforces the critical flow: edit → local validation → MANDATORY dry run →
 * apply (with confirmation) → monitor. Apply is only reachable after a
 * successful dry run of the current diff; any edit invalidates it.
 */

import type { CapacityTaskResponse } from '../types/outposts';

export type DryRunPhase =
  | 'editing'
  | 'dryRunPending'
  | 'dryRunPassed'
  | 'dryRunFailed'
  | 'applyPending'
  | 'applied'
  | 'applyFailed';

export interface DryRunState {
  phase: DryRunPhase;
  /** Last dry-run result (present in dryRunPassed/dryRunFailed). */
  dryRun?: CapacityTaskResponse;
  /** Apply result (present in applied). */
  applied?: CapacityTaskResponse;
  /** Transport/apply error message. */
  error?: string;
  /** True when the apply failure was a ConflictException. */
  conflict?: boolean;
}

export type DryRunEvent =
  | { type: 'EDIT' }
  | { type: 'DRY_RUN_START' }
  | { type: 'DRY_RUN_RESULT'; result: CapacityTaskResponse }
  | { type: 'DRY_RUN_ERROR'; error: string }
  | { type: 'APPLY_START' }
  | { type: 'APPLY_SUCCESS'; result: CapacityTaskResponse }
  | { type: 'APPLY_ERROR'; error: string; conflict?: boolean };

export const initialDryRunState: DryRunState = { phase: 'editing' };

/** A dry run passes when the service reports COMPLETED without a failure. */
export function dryRunPassed(result: CapacityTaskResponse): boolean {
  return result.status === 'COMPLETED' && result.failed === undefined;
}

/** Apply is allowed only after a successful dry run (or retrying a failed apply). */
export function canApply(state: DryRunState): boolean {
  return state.phase === 'dryRunPassed' || state.phase === 'applyFailed';
}

/** Dry run can be requested while editing or re-run after a result. */
export function canRunDryRun(state: DryRunState): boolean {
  return (
    state.phase === 'editing' ||
    state.phase === 'dryRunPassed' ||
    state.phase === 'dryRunFailed' ||
    state.phase === 'applyFailed'
  );
}

export function dryRunReducer(state: DryRunState, event: DryRunEvent): DryRunState {
  switch (event.type) {
    case 'EDIT':
      // Editing invalidates any dry-run result — back to the gate
      if (state.phase === 'dryRunPending' || state.phase === 'applyPending') return state;
      return { phase: 'editing' };

    case 'DRY_RUN_START':
      return canRunDryRun(state) ? { phase: 'dryRunPending' } : state;

    case 'DRY_RUN_RESULT':
      if (state.phase !== 'dryRunPending') return state;
      return dryRunPassed(event.result)
        ? { phase: 'dryRunPassed', dryRun: event.result }
        : { phase: 'dryRunFailed', dryRun: event.result };

    case 'DRY_RUN_ERROR':
      if (state.phase !== 'dryRunPending') return state;
      return { phase: 'dryRunFailed', error: event.error };

    case 'APPLY_START':
      return canApply(state) ? { phase: 'applyPending', dryRun: state.dryRun } : state;

    case 'APPLY_SUCCESS':
      if (state.phase !== 'applyPending') return state;
      return { phase: 'applied', applied: event.result };

    case 'APPLY_ERROR':
      if (state.phase !== 'applyPending') return state;
      return { phase: 'applyFailed', error: event.error, conflict: event.conflict === true };

    default:
      return state;
  }
}
